import { todayISO } from "./dateFormat.js";
// Schedule + attendance helpers shared by teacher attendance and student my-attendance views.

export const DAY_CODES = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]; // Monday-first canonical

/**
 * Returns ISO dates within the same month as baseISO that match any of scheduleDays
 * (canonical codes: mon..sun). Optional `fromISO` clips out dates before that boundary
 * (use it to respect group creation or student enrollment start dates).
 */
export function expectedDatesInMonth(scheduleDays, baseISO, fromISO = null) {
  if (!scheduleDays || scheduleDays.length === 0) return [];
  const base = new Date(baseISO + "T00:00:00");
  const year = base.getFullYear();
  const month = base.getMonth();
  const last = new Date(year, month + 1, 0).getDate();
  const want = new Set(scheduleDays);
  const out = [];
  for (let d = 1; d <= last; d++) {
    const dt = new Date(year, month, d);
    const code = DAY_CODES[(dt.getDay() + 6) % 7]; // Mon=0..Sun=6
    if (want.has(code)) {
      const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      if (fromISO && iso < fromISO) continue;
      out.push(iso);
    }
  }
  return out;
}

export function formatDayLabel(iso, lang = "es") {
  const dt = new Date(iso + "T00:00:00");
  return dt.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { weekday: "short", day: "2-digit", month: "short" });
}

/**
 * Iterates scheduled dates from fromISO forward, up to maxIterations.
 * The kickoff date (fromISO) is always included even if its weekday isn't in scheduleDays.
 */
export function nextScheduledDates(scheduleDays, fromISO, maxIterations = 60) {
  if (!scheduleDays || scheduleDays.length === 0) return [fromISO].filter(Boolean);
  const want = new Set(scheduleDays);
  const dates = new Set();
  if (fromISO) dates.add(fromISO);
  const start = new Date((fromISO || todayISO()) + "T00:00:00");
  for (let i = 0; i < maxIterations; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const code = DAY_CODES[(d.getDay() + 6) % 7];
    if (want.has(code)) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (!fromISO || iso >= fromISO) dates.add(iso);
    }
  }
  return Array.from(dates).sort();
}

/**
 * Walks a chronological list of scheduled dates and assigns each one a 1-based
 * `cycle` index based on the school's consumption rules:
 *   - present | absent | unjustified | unjustified_consumed → consumes 1
 *   - justified above the per-cycle threshold                → consumes 1
 *   - teacher_cancelled                                      → never consumes
 *   - future / no record                                     → assumed to consume (worst case)
 *
 * Returns: [{ date, status?, cycle, consumes }]
 *
 * `planChanges` (enrollment.plan_changes): [{ at_consumed, from_classes, to_classes }].
 * Los ciclos ya cursados se cuentan con su tamaño original; el nuevo tamaño aplica
 * a partir de `at_consumed` sesiones acumuladas (fin del ciclo en curso al cambiar).
 */
function planSizeAt(totalConsumed, base, changes) {
  let size = base;
  for (const c of changes) {
    if (Number(c.at_consumed) <= totalConsumed) size = Number(c.to_classes);
  }
  return size;
}

/**
 * Pausa con fechas: devuelve la pausa activa que cubre `dateISO`, o null.
 * `to_date` vacío = pausa abierta (no sabe cuándo regresa).
 */
export function pauseOn(pauses, dateISO) {
  for (const p of pauses || []) {
    if (!p || p.cancelled) continue;
    const from = (p.from_date || "").slice(0, 10);
    if (!from || dateISO < from) continue;
    const to = (p.to_date || "").slice(0, 10);
    if (!to || dateISO <= to) return p;
  }
  return null;
}

export function classifyDatesByCycle(dates, attMap, totalPerCycle, threshold = 0, startsOnDay = 0, planChanges = [], pausedDates = null) {
  const changes = (planChanges || [])
    .filter((c) => c && Number(c.to_classes) > 0)
    .sort((a, b) => Number(a.at_consumed) - Number(b.at_consumed));
  const base = (changes.length && Number(changes[0].from_classes) > 0) ? Number(changes[0].from_classes) : totalPerCycle;
  let cycle = 1;
  let consumedInCycle = Math.max(0, Math.min(Number(startsOnDay) || 0, Math.max(0, (base || 0) - 1)));
  let totalConsumed = consumedInCycle;
  let size = planSizeAt(totalConsumed, base, changes);
  let justifiedInCycle = 0;
  const out = [];
  for (const date of dates) {
    const status = attMap.get(date);
    let consumes = false;
    if (pausedDates && pausedDates.has(date)) {
      // Pausa con fechas (viaje): la sesión se congela, no consume el ciclo y
      // las que faltan se corren hasta el regreso del estudiante.
      out.push({ date, status, cycle, consumes: false, paused: true });
      continue;
    }
    if (
      status === "present" ||
      status === "absent" ||
      status === "unjustified" ||
      status === "unjustified_consumed"
    ) {
      consumes = true;
    } else if (status === "justified") {
      // Justified absences NEVER consume — the school explicitly absorbs
      // the class (either auto-cancel in-window or admin-approved).
      justifiedInCycle++;
      consumes = false;
    } else if (status === "teacher_cancelled" || status === "not_counted" || status === "removed") {
      consumes = false;
    } else {
      // future or no record → assume it will consume
      consumes = true;
    }
    out.push({ date, status, cycle, consumes });
    if (consumes && size > 0) {
      consumedInCycle++;
      totalConsumed++;
      if (consumedInCycle >= size) {
        cycle++;
        consumedInCycle = 0;
        justifiedInCycle = 0;
        size = planSizeAt(totalConsumed, base, changes);
      }
    }
  }
  return out;
}


/**
 * SINGLE SOURCE OF TRUTH for "what does this student's CURRENT cycle look like".
 *
 * Every screen that needs the student's cycle (progress card, "Mi ciclo actual"
 * chips, the cancellation date picker) MUST use this helper. Computing it
 * ad-hoc is what caused the cancellation picker to drift from the progress card
 * (students in cycle >= 2 got 0 selectable dates because consumption was counted
 * across their whole history instead of within the cycle).
 *
 * Returns null when the cycle can't be determined (no start date / unknown size).
 * {
 *   cycle,        // 1-based index of the cycle containing today
 *   dates,        // [{date, status, consumes}] of that cycle only
 *   classified,   // every known date with its cycle (for cycle navigation)
 *   swapMap,      // reschedules applied: newDate -> {original, meta}
 *   consumed, total, remaining, pct
 * }
 */
export function currentCycleDates({ group, enrollment, extraSessions = [], attendance = [], totalPerCycle = 0, today = todayISO(), scope = "student", pauses = null }) {
  if (!group || !enrollment) return null;
  const total = Number(totalPerCycle) || 0;
  if (total <= 0) return null;
  const startISO = enrollment.start_date || (group.created_at ? group.created_at.slice(0, 10) : null);
  if (!startISO) return null;

  const selfBooking = !!enrollment.self_booking || !(group.schedule_days || []).length;
  // `scope: "group"` is for the teacher/admin strip of a whole group: every
  // session of the group counts, no matter which student it belongs to.
  const mine = (x) => scope === "group" || !x.enrollment_id || x.enrollment_id === enrollment.id;
  const active = (extraSessions || []).filter(
    (x) => x.group_id === group.id && (x.status || "active") === "active" && mine(x),
  );

  let allDates;
  let swapMap = {};
  if (selfBooking) {
    // The cycle is made of the classes the student booked themselves.
    allDates = [...new Set(active.filter((x) => x.type === "extra").map((x) => x.date))]
      .filter((d) => d >= startISO).sort();
  } else {
    // La ventana de proyección arranca en la FECHA DE INICIO, así que tiene que
    // cubrir todo lo ya transcurrido + el ciclo que se está mirando. Cuando solo
    // se proyectaba `total * 14` días desde el inicio, a los estudiantes viejos
    // se les cortaba el ciclo actual y mostraba menos fechas de las que tiene
    // (caso Reiko: ciclo de 10 mostrando 8 fechas).
    const elapsedDays = Math.max(
      0,
      Math.round((new Date(today + "T00:00:00") - new Date(startISO + "T00:00:00")) / 86400000),
    );
    const horizon = Math.max(60, elapsedDays + total * 14 + 30);
    allDates = nextScheduledDates(group.schedule_days || [], startISO, horizon);
    const swapped = applyReschedules(allDates, active.filter((x) => x.type === "reschedule" && x.original_class_date));
    allDates = swapped.dates;
    swapMap = swapped.swapMap;
    // Extra / makeup sessions that consume a slot ARE classes of the cycle.
    const extras = active
      .filter((x) => x.type !== "reschedule" && x.counts_toward_cycle !== false)
      .map((x) => x.date)
      .filter((d) => d >= startISO);
    if (extras.length) allDates = [...new Set([...allDates, ...extras])].sort();
  }
  if (!allDates.length) return null;

  const attMap = new Map();
  (attendance || []).filter((a) => a.group_id === group.id).forEach((a) => attMap.set(a.date, a.status));
  const threshold = Math.max(1, Math.floor(total * 0.25));
  const pauseList = pauses || enrollment.pauses || [];
  const pausedDates = pauseList.length
    ? new Set(allDates.filter((d) => !!pauseOn(pauseList, d)))
    : null;
  const classified = classifyDatesByCycle(
    allDates, attMap, total, threshold, Number(enrollment.starts_on_day || 0), enrollment.plan_changes,
    pausedDates,
  );

  let cycle = 1;
  for (const d of classified) {
    if (d.date <= today) cycle = d.cycle;
    else break;
  }
  const dates = classified.filter((d) => d.cycle === cycle);
  const preConsumed = cycle === 1 ? Number(enrollment.starts_on_day || 0) : 0;
  const consumed = dates.filter((d) => d.status && d.consumes).length + preConsumed;
  const remaining = Math.max(0, total - consumed);
  return {
    cycle, dates, classified, swapMap,
    consumed, total, remaining,
    pct: Math.round((consumed / total) * 100),
  };
}


/**
 * Given a chronological list of regularly scheduled ISO dates and a list of
 * active "reschedule" sessions (each with `original_class_date` → `date`),
 * returns a new sorted list where each reschedule has replaced its original
 * occurrence. Reschedules whose `original_class_date` is NOT in the input
 * are ignored (nothing to swap against — the caller can decide to append
 * them as extras).
 *
 * `studentId` (optional): when set, per-student reschedules whose
 * `enrollment_id`'s student doesn't match are ignored. Group-wide
 * reschedules (no enrollment_id / no studentByEnrollment mapping) always
 * apply.
 *
 * @param {string[]} dates
 * @param {Array<{date:string, original_class_date:string, enrollment_id?:string, status?:string}>} reschedules
 * @param {{studentByEnrollment?: Record<string,string>, studentId?: string}} [opts]
 * @returns {{dates: string[], swapMap: Record<string, {original: string, meta: object}>}}
 */
export function applyReschedules(dates, reschedules, opts = {}) {
  const active = (reschedules || []).filter((r) => r && r.original_class_date && r.date && (r.status || "active") === "active");
  if (active.length === 0) return { dates: [...dates], swapMap: {} };
  const { studentByEnrollment = {}, studentId = null } = opts;
  const set = new Set(dates);
  const swapMap = {};
  for (const r of active) {
    // Filter by student when relevant
    if (studentId && r.enrollment_id) {
      const rStu = studentByEnrollment[r.enrollment_id];
      if (rStu && rStu !== studentId) continue;
    }
    if (!set.has(r.original_class_date)) continue; // nothing to swap
    set.delete(r.original_class_date);
    set.add(r.date);
    swapMap[r.date] = { original: r.original_class_date, meta: r };
  }
  return { dates: Array.from(set).sort(), swapMap };
}
